// OpenerOverlay (public) — fixed full-viewport overlay above the invitation.
// Owns: scroll lock, skip button, seen-once localStorage, preview/draft
// bypass, prefers-reduced-motion detection, and (Phase 1.9.18) the
// tap-gate that makes the opening itself the music gesture when an
// invitation has audio configured.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import OpenerHost from './OpenerHost';

const PREFERS_REDUCED = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
  : false;

// ─── Centralized scroll lock helpers ───────────────────────────────────
function lockPageScroll() {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.add('has-active-opener');
  document.body.classList.add('has-active-opener');
}

function unlockPageScroll() {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.remove('has-active-opener');
  document.body.classList.remove('has-active-opener');
  const htmlS = document.documentElement.style;
  const bodyS = document.body.style;
  htmlS.removeProperty('overflow');
  htmlS.removeProperty('overscroll-behavior');
  htmlS.removeProperty('touch-action');
  bodyS.removeProperty('overflow');
  bodyS.removeProperty('position');
  bodyS.removeProperty('top');
  bodyS.removeProperty('width');
  bodyS.removeProperty('overscroll-behavior');
  bodyS.removeProperty('touch-action');
}

export function shouldPlayOpener({ eventId, opener, isPreviewOrDraft }) {
  if (!opener || !opener.presetId) return false;
  if (opener.enabled === false) return false;
  if (typeof window === 'undefined') return false;
  if (isPreviewOrDraft) return true;
  try {
    return !window.localStorage.getItem(`opener-seen-${eventId}`);
  } catch {
    return true;
  }
}

/**
 * @param {Object} props
 * @param {string} props.eventId
 * @param {Object} props.opener
 * @param {boolean} [props.gestureGated]
 *   When true the overlay shows the preset's first frame + a subtle
 *   "Tap to open" cue and waits for the guest's tap before starting the
 *   animation timeline. Used when an invitation has audio so the same
 *   gesture satisfies browser autoplay policy. When false (default) the
 *   opener auto-starts on mount, preserving legacy behavior.
 * @param {() => (Promise<boolean> | boolean)} [props.onTap]
 *   Invoked when the guest taps the gate. Resolves to whether audio
 *   playback actually started (used only for logging; the timeline runs
 *   regardless so failures gracefully degrade per spec).
 * @param {{ current: (() => void) | null }} [props.externalStartRef]
 *   Phase 1.9.19 — when provided, the overlay yields control of the
 *   start gesture to an external surface (Entry Cover). The internal
 *   tap cue is hidden and the overlay no longer accepts taps; the
 *   external surface is expected to call externalStartRef.current()
 *   to begin the preset timeline. Mutually exclusive with gestureGated.
 * @param {boolean} [props.showSkip]
 *   Phase 1.9.21 — when false (default), the Skip button is never
 *   rendered and its visibility timer never starts. The Entry Cover →
 *   short opening flow doesn't need a Skip affordance since the guest
 *   already opted in. Longer presets can opt back in by passing true.
 * @param {() => void} [props.onExitStart]
 * @param {() => void} [props.onComplete]
 */
export default function OpenerOverlay({
  eventId,
  opener,
  gestureGated = false,
  onTap,
  externalStartRef,
  showSkip = false,
  onExitStart,
  onComplete,
}) {
  // Phase 1.9.19 — external gating wins over the internal tap-gate.
  // When externalStartRef is provided, autoStart is forced false and
  // the cue + click handler are suppressed (the Entry Cover renders
  // its own cue and owns the gesture).
  const externallyGated = !!externalStartRef;
  const effectiveGestureGated = !externallyGated && gestureGated;

  const [skipReady, setSkipReady] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  // Phase 1.9.18 — tap-gate state. When gestureGated is true the timer
  // for Skip + the imperative start() call only run AFTER the guest taps.
  // Phase 1.9.19 — when externallyGated, tapped flips when the external
  // ref's start() is invoked (we wrap the assignment below).
  const [tapped, setTapped] = useState(!effectiveGestureGated && !externallyGated);
  const internalStartRef = useRef(null);
  const completedRef = useRef(false);

  // Phase 1.9.19 — choose which startRef the OpenerHost gets:
  // externally-supplied (cover-owned) or internal (self-owned). When the
  // external ref is used, wrap its setter so we still flip `tapped` so
  // Skip + completion logic remains identical.
  const startRefForHost = useMemo(() => {
    if (!externallyGated) return internalStartRef;
    return {
      get current() { return externalStartRef.current; },
      set current(fn) {
        externalStartRef.current = (...args) => {
          setTapped(true);
          return fn?.(...args);
        };
      },
    };
  }, [externallyGated, externalStartRef]);

  // Scroll lock on mount, unlock on unmount.
  useEffect(() => {
    lockPageScroll();
    return () => { unlockPageScroll(); };
  }, []);

  // Skip becomes available 1.5 s after the OPENING ACTUALLY STARTS.
  // Per Phase 1.9.18 spec, the gate itself never shows Skip — there is
  // nothing to skip until the guest has tapped.
  // Phase 1.9.21 — additionally gated on `showSkip`. When false (the
  // default for the Entry Cover → short opener flow) the timer never
  // runs and the button never renders.
  useEffect(() => {
    if (!showSkip || !tapped) return undefined;
    const t = setTimeout(() => setSkipReady(true), 1500);
    return () => clearTimeout(t);
  }, [showSkip, tapped]);

  const FADE_MS = 1100;
  const complete = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    setFadingOut(true);
    try { window.localStorage.setItem(`opener-seen-${eventId}`, '1'); } catch { /* ignore */ }
    unlockPageScroll();
    try { onExitStart?.(); } catch { /* ignore */ }
    setTimeout(() => { onComplete?.(); }, FADE_MS + 60);
  };

  // Phase 1.9.18 — handler invoked on the guest tap. We trigger the
  // preset's imperative start() FIRST so the animation begins in the
  // same frame as the tap, then call onTap() (which kicks audio). If
  // audio.play() rejects, the animation continues anyway per spec.
  const handleTap = async () => {
    if (tapped) return;
    setTapped(true);
    try { internalStartRef.current?.(); } catch { /* ignore preset errors */ }
    try { await onTap?.(); } catch { /* ignore audio errors */ }
  };

  const exitMs = PREFERS_REDUCED ? 250 : FADE_MS;

  return (
    <div
      className={fadingOut ? 'opener-overlay is-exiting' : 'opener-overlay'}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        minHeight: '100dvh',
        zIndex: 9999,
        background: 'transparent',
        opacity: fadingOut ? 0 : 1,
        pointerEvents: fadingOut ? 'none' : 'auto',
        transition: `opacity ${exitMs}ms ease`,
        // Phase 1.9.18 — accessible tap target. The whole overlay catches
        // the tap during self-gate mode; afterwards it's just a passive
        // layer. When externally gated (Entry Cover) the overlay never
        // accepts taps.
        cursor: effectiveGestureGated && !tapped ? 'pointer' : 'default',
      }}
      onClick={effectiveGestureGated && !tapped ? handleTap : undefined}
      role={effectiveGestureGated && !tapped ? 'button' : undefined}
      tabIndex={effectiveGestureGated && !tapped ? 0 : undefined}
      onKeyDown={effectiveGestureGated && !tapped ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTap(); } } : undefined}
      aria-label={effectiveGestureGated && !tapped ? 'Tap to open invitation' : undefined}
      data-testid="opener-overlay"
    >
      <OpenerHost
        presetId={opener.presetId}
        version={opener.version}
        settings={opener.settings}
        reducedMotion={PREFERS_REDUCED}
        autoStart={!externallyGated && (!gestureGated || tapped)}
        startRef={startRefForHost}
        onComplete={complete}
        onError={complete}
      />
      {/* Phase 1.9.18 — subtle Tap-to-open cue. Visible only before the
          first tap in self-gate mode. Hidden when externally gated
          (Entry Cover renders its own cue). */}
      {effectiveGestureGated && !tapped && (
        <TapToOpenCue />
      )}
      {skipReady && !fadingOut && showSkip && (
        <button
          onClick={(e) => { e.stopPropagation(); complete(); }}
          aria-label="Skip opening animation"
          data-testid="opener-skip-btn"
          style={{
            position: 'absolute',
            top: 'max(16px, env(safe-area-inset-top))',
            right: 'max(16px, env(safe-area-inset-right))',
            zIndex: 10000,
            background: 'rgba(255,255,255,0.6)',
            backdropFilter: 'blur(6px)',
            border: '1px solid rgba(80,68,52,0.18)',
            color: 'rgba(42,36,30,0.78)',
            padding: '6px 12px',
            borderRadius: '999px',
            fontSize: '11px',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            opacity: 0.85,
            transition: 'opacity 0.2s',
          }}
        >
          Skip
        </button>
      )}
    </div>
  );
}

// Phase 1.9.18 — subtle pulse glyph + small "Tap to open" caption.
// CSS-only keyframes injected once. Centered near the lower part of the
// opening, restrained typography, no heavy overlay or button container.
let _cueInjected = false;
function injectCueStyles() {
  if (_cueInjected || typeof document === 'undefined') return;
  _cueInjected = true;
  const style = document.createElement('style');
  style.setAttribute('data-opener-cue', '1');
  style.textContent = `
    @keyframes opener-cue-pulse {
      0%   { opacity: 0.35; transform: scale(0.92); }
      50%  { opacity: 0.85; transform: scale(1.06); }
      100% { opacity: 0.35; transform: scale(0.92); }
    }
    @keyframes opener-cue-fade-in {
      from { opacity: 0; transform: translate(-50%, 6px); }
      to   { opacity: 1; transform: translate(-50%, 0); }
    }
    .opener-cue {
      position: absolute;
      left: 50%;
      bottom: 14%;
      transform: translateX(-50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      pointer-events: none;
      z-index: 10000;
      animation: opener-cue-fade-in 700ms ease 600ms both;
    }
    .opener-cue-glyph {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: rgba(42, 36, 30, 0.55);
      box-shadow: 0 0 0 6px rgba(42, 36, 30, 0.10);
      animation: opener-cue-pulse 2.2s ease-in-out infinite;
    }
    .opener-cue-caption {
      font-size: 10px;
      letter-spacing: 0.28em;
      text-transform: uppercase;
      color: rgba(42, 36, 30, 0.70);
      font-family: 'Cormorant Garamond', Georgia, serif;
    }
  `;
  document.head.appendChild(style);
}

function TapToOpenCue() {
  useEffect(() => { injectCueStyles(); }, []);
  return (
    <div className="opener-cue" data-testid="opener-tap-cue">
      <div className="opener-cue-glyph" />
      <div className="opener-cue-caption">Tap to open</div>
    </div>
  );
}
